/* eslint-disable @typescript-eslint/naming-convention */
import { useMutation } from '@apollo/client';
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import {
  Admonition,
  AdmonitionBody,
  AdmonitionHeader,
  AdmonitionTitle,
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Code as OrbitCode,
  Text,
} from '@apollo/orbit';
import React, { useEffect, useState } from 'react';
import { Link as ReactRouterLink, useHistory } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import {
  Step,
  StepContent,
} from 'src/app/account/supergraphListPage/emptyGraphLayout/components/StepContent';
import { GenerateAPIKeyMutation } from 'src/app/graph/graphSettingsPage/apiKeysPage/apiKeysCard/ApiKeysModal';
import { variantSettingsRouteConfig } from 'src/app/graph/routes';
import { Code } from 'src/components/code/Code';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { useShowToast } from 'src/components/toast/Toast';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { EndpointInput } from '../components/EndpointInput';
import { getPlatformById } from '../config/platforms';
import { UpdateVariantURLMutation } from '../mutations';

/**
 * Page for platform-specific deployment instructions with step-by-step guidance
 * Shows deployment instructions for Railway, Render, and other supported platforms
 */
export const DeploymentInstructionsPage = () => {
  const {
    graphId,
    variantName,
    platform: platformId,
  } = useRouteParams(orgRoutes.deploymentInstructions);
  const [accountId] = useCurrentAccountId();
  const [endpointUrl, setEndpointUrl] = useState('');
  const [isValidEndpoint, setIsValidEndpoint] = useState(false);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [newKey, setNewKey] = useState<NonNullable<
    NonNullable<GraphQLTypes.GenerateAPIKeyMutation['service']>['newKey']
  > | null>(null);
  const [valueToAdd, setValueToAdd] = useState<string>('');
  const history = useHistory();
  const { showToasts } = useShowToast();

  // Track page view
  useEffect(() => {
    if (graphId && variantName && platformId) {
      ampli.deploymentInstructionsPageViewed({
        graph_ref: `${graphId}@${variantName}`,
        deployment_platform: platformId,
      });
    }
  }, [graphId, variantName, platformId]);

  // Extract navigation paths to avoid duplication (only if accountId exists)
  const deploymentFlowPath = accountId
    ? orgRoutes.deploymentFlow.path({
        orgId: accountId,
        graphId,
        variantName,
      })
    : null;

  // Helper function to reset modal state
  const resetModalState = () => {
    setIsApiKeyModalOpen(false);
    setValueToAdd('');
  };

  const [updateVariantURL, { loading: isSavingEndpoint }] = useMutation<
    GraphQLTypes.QuickDeploy__UpdateVariantURLMutation,
    GraphQLTypes.QuickDeploy__UpdateVariantURLMutationVariables
  >(UpdateVariantURLMutation);

  const [addKeyMutation, { loading: addKeyLoading }] = useMutation<
    GraphQLTypes.GenerateAPIKeyMutation,
    GraphQLTypes.GenerateAPIKeyMutationVariables
  >(GenerateAPIKeyMutation, {
    onCompleted: (returnData) => {
      setNewKey(returnData.service?.newKey || null);
    },
    onError: (apiKeyError) => {
      ampli.deploymentFlowError({
        graph_ref: `${graphId}@${variantName}`,
        error_message: apiKeyError.message,
        deployment_platform: platformId || 'unknown',
      });

      showToasts({
        heading: 'Failed to create API key',
        level: 'error',
        dismissable: true,
        message:
          "We couldn't create your API key. Please check your connection and try again.",
      });
    },
  });

  // Validate route parameters - if invalid, show error instead of infinite loading
  if (!graphId || !variantName) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Heading as="h1" size="xl" className="mb-4">
            Invalid Graph or Variant
          </Heading>
          <Text className="mb-6">
            The graph ID or variant name in the URL is invalid.
          </Text>
          <Button
            variant="primary"
            onClick={() =>
              history.push(orgRoutes.graphs.path({ orgId: accountId || '' }))
            }
          >
            Back to Graphs
          </Button>
        </div>
      </div>
    );
  }

  // Only show loading for temporary accountId absence
  if (!accountId) {
    return <Loading />;
  }

  const graphRef = `${graphId}@${variantName}`;

  const handleFinishSetup = async () => {
    try {
      await updateVariantURL({
        variables: {
          graph: graphId,
          variant: variantName,
          url: endpointUrl.trim(),
        },
      });

      // Track successful endpoint update
      ampli.endpointUpdated({
        graph_ref: `${graphId}@${variantName}`,
        endpoint_changed: true,
        update_source: 'create_endpoint_instructions_page',
      });

      // Navigate to graphs page with success modal using overlay pattern
      history.push({
        ...setOverlayVisibleLocation({
          location: {
            ...orgRoutes.graphs.location({ orgId: accountId }),
            state: {
              graphId,
              variantName,
            },
          },
          overlayName: Config.modals.endpointAddedConfirmation,
        }),
      });
    } catch (error) {
      ampli.updateEndpointError({
        graph_ref: `${graphId}@${variantName}`,
        error_name: error instanceof Error ? error.message : 'Unknown error',
        update_source: 'create_endpoint_instructions_page',
      });

      showToasts({
        heading: 'Failed to save endpoint',
        level: 'error',
        dismissable: true,
        message:
          "We couldn't save your endpoint URL. Please check your connection and try again.",
      });
    }
  };

  const config = getPlatformById(platformId || '') || {
    id: platformId || 'unknown',
    name: platformId
      ? platformId.charAt(0).toUpperCase() + platformId.slice(1)
      : 'Platform',
    type: 'guided' as const,
    action: 'Deploy',
    templateUrl: '#',
    defaultEndpoint: 'https://example.com',
    description: `Deploy a GraphOS Router using ${platformId || 'this platform'}.`,
  };

  // Don't set default endpoint as actual value - let it remain as placeholder

  return (
    <div className="h-screen overflow-auto bg-primary">
      <div className="mx-auto max-w-3xl p-8">
        {deploymentFlowPath && (
          <Button
            as={ReactRouterLink}
            to={deploymentFlowPath}
            variant="hidden"
            leftIcon={<IconArrowLeft />}
            className="mb-8 p-0"
          >
            Back to deployment options
          </Button>
        )}

        {/* Page header */}
        <div className="mb-8">
          <Heading as="h1" variant="heading" size="3xl" className="mb-4">
            Deploy {graphId}@{variantName} with {config.name}
          </Heading>
        </div>

        <StepContent>
          <Step id="create-api-key">
            <div className="flex flex-col gap-4">
              <h3 className="text-heading-xl pt-1">
                Gather your environment variables
              </h3>

              <div className="mb-6">
                <Text variant="body" className="text-title mb-2">
                  Your APOLLO_GRAPH_REF
                </Text>
                <OrbitCode
                  canCopy
                  className="h-12 w-full justify-between px-4 py-6"
                >
                  {graphRef}
                </OrbitCode>
              </div>

              <div className="mb-2">
                <Text variant="body" className="text-title mb-2">
                  Your APOLLO_KEY
                </Text>
                <Text variant="body" className="mb-4 text-secondary">
                  This key was generated for you when you created your graph
                  variant. Make sure you have it handy, or create a new key
                  below.
                </Text>

                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsApiKeyModalOpen(true);
                    setValueToAdd('');
                  }}
                  className="mt-2"
                >
                  Generate graph API key
                </Button>
                {newKey && (
                  <FormControl className="mt-4">
                    <FormHelperText iconType="success">
                      <Text variant="bodySb" size="sm" as="span">
                        {newKey.keyName}
                      </Text>{' '}
                      was generated successfully. Graph API keys can be managed
                      in your graph's settings section.
                    </FormHelperText>
                  </FormControl>
                )}
              </div>
            </div>
          </Step>

          <Step id="deploy-on-platform">
            <div className="flex flex-col gap-4">
              <h3 className="text-heading-xl pt-1">
                Head over to {config.name} to deploy your graph
              </h3>
              <Text variant="body" className="mb-3 text-primary">
                You'll be using Apollo's router template to deploy with one
                click.
              </Text>

              <Button
                variant="secondary"
                rightIcon={<IconOutlink />}
                onClick={() => window.open(config.templateUrl, '_blank')} // nosemgrep
                className="w-fit"
              >
                Deploy on {config.name}
              </Button>
            </div>
          </Step>

          <Step id="save-endpoint-url">
            <div className="flex flex-col gap-4">
              <h3 className="text-heading-xl pt-1">
                Enter and save your graph variant's URL
              </h3>
              <Text variant="body" className="text-primary">
                Once you've deployed the Apollo Router and obtained an endpoint
                URL, please enter it below.
              </Text>

              <EndpointInput
                value={endpointUrl}
                onChange={setEndpointUrl}
                placeholder={config.defaultEndpoint}
                label="Endpoint URL"
                isRequired
                onValidationChange={(isValid) => {
                  setIsValidEndpoint(isValid);
                }}
              />
            </div>
            <Admonition variant="info" className="mt-8">
              <AdmonitionHeader>
                <AdmonitionTitle>Updating your endpoint</AdmonitionTitle>
              </AdmonitionHeader>
              <AdmonitionBody>
                If you need to update your endpoint in the future, add headers,
                or access other advanced options, you can do so by visiting{' '}
                <Link
                  to={variantSettingsRouteConfig.path({
                    orgId: accountId,
                    graphId,
                    graphVariant: variantName,
                    tab: 'explorer',
                  })}
                >
                  your variant's settings page
                </Link>{' '}
                .
              </AdmonitionBody>
            </Admonition>
          </Step>
        </StepContent>

        {/* Action buttons */}
        <div className="-mt-12 flex items-center gap-3 pb-6 pt-2">
          {deploymentFlowPath && (
            <Button
              as={ReactRouterLink}
              to={deploymentFlowPath}
              variant="secondary"
            >
              Back to deployment options
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleFinishSetup}
            isDisabled={!endpointUrl.trim() || !isValidEndpoint}
            isLoading={isSavingEndpoint}
            loadingText="Saving endpoint"
          >
            Save endpoint
          </Button>
        </div>

        {/* API Key Creation Modal */}
        {isApiKeyModalOpen && (
          <Modal isOpen onClose={resetModalState} size="xl">
            <ModalOverlay />
            <ModalContent>
              <CustomForm
                onSubmit={async (event) => {
                  const name = new FormData(event.currentTarget).get('name');
                  if (typeof name !== 'string') {
                    // Early return if form data is invalid
                    return;
                  }
                  try {
                    await addKeyMutation({
                      variables: {
                        serviceId: graphId,
                        name,
                        role: GraphQLTypes.UserPermission.GRAPH_ADMIN,
                      },
                    });
                  } catch (error) {
                    // Handle any JavaScript-level errors not caught by onError
                    showToasts({
                      heading: 'Failed to create API key',
                      level: 'error',
                      dismissable: true,
                      message:
                        "We couldn't create your API key. Please try again.",
                    });
                  }
                }}
              >
                <ModalHeader>
                  <ModalTitle>New API Key</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  {newKey ? (
                    <>
                      <Alert status="info">
                        Make sure you store your API key safely. You won't be
                        able to see it once you leave this page.
                      </Alert>
                      <Code
                        showCopyButton
                        code={newKey.token}
                        className="flex justify-between rounded"
                      />
                    </>
                  ) : (
                    <FormControl>
                      <FormLabel>Key name</FormLabel>
                      <Input
                        type="text"
                        autoFocus
                        value={valueToAdd}
                        onChange={(e) => setValueToAdd(e.target.value)}
                        name="name"
                      />
                    </FormControl>
                  )}
                </ModalBody>
                <ModalFooter>
                  {newKey ? (
                    <Button variant="primary" onClick={resetModalState}>
                      Close
                    </Button>
                  ) : (
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={resetModalState}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isDisabled={valueToAdd?.length === 0}
                        isLoading={addKeyLoading}
                        loadingText="Create"
                      >
                        Create
                      </Button>
                    </ButtonGroup>
                  )}
                </ModalFooter>
              </CustomForm>
            </ModalContent>
          </Modal>
        )}
      </div>
    </div>
  );
};
